import { Promotion, PromotionFilters, ActivatePromoCodeResponse, UserActivatedPromotion, UserTriggeredPromotion } from '@lunit/shared-types';
import { api } from './client';

export async function getPromotions(
  venueId: string,
  filters?: PromotionFilters
): Promise<Promotion[]> {
  const params = new URLSearchParams({ venueId });

  if (filters?.isActive !== undefined) {
    params.append('isActive', String(filters.isActive));
  }

  if (filters?.isFeatured !== undefined) {
    params.append('isFeatured', String(filters.isFeatured));
  }

  if (filters?.type) {
    params.append('type', filters.type);
  }

  if (filters?.userId) {
    params.append('userId', filters.userId);
  }

  // Filter by activation type if specified
  // Note: We don't filter by default anymore - API returns all types
  // PROMO_CODE promotions will appear in activated promotions after user enters the code
  if (filters?.activationType) {
    params.append('activationType', filters.activationType);
  }
  // If no activationType filter, API returns all (AUTO, OPT_IN, PROMO_CODE)
  // We'll filter PROMO_CODE on the client side

  return api.get<Promotion[]>(`/promotions?${params.toString()}`);
}

export async function getPromotion(id: string): Promise<Promotion> {
  return api.get<Promotion>(`/promotions/${id}`);
}

/**
 * Activate a promo code for the user
 */
export async function activatePromoCode(
  code: string,
  userId: string,
  venueId: string
): Promise<ActivatePromoCodeResponse> {
  return api.post<ActivatePromoCodeResponse>('/promotions/activate', {
    code,
    userId,
    venueId
  });
}

/**
 * Activate an OPT_IN promotion for the user
 * User explicitly opts-in to participate in the promotion
 */
export async function activatePromotion(
  promotionId: string,
  userId: string
): Promise<ActivatePromoCodeResponse> {
  return api.post<ActivatePromoCodeResponse>(`/promotions/${promotionId}/activate`, {
    userId
  });
}

/**
 * Get user's activated promo code promotions
 */
export async function getActivatedPromotions(
  userId: string,
  venueId?: string,
  includeUsed: boolean = false
): Promise<UserActivatedPromotion[]> {
  const params = new URLSearchParams();
  if (venueId) {
    params.append('venueId', venueId);
  }
  if (includeUsed) {
    params.append('includeUsed', 'true');
  }
  return api.get<UserActivatedPromotion[]>(`/users/${userId}/activated-promotions?${params.toString()}`);
}

/**
 * Get user's triggered promotions (personal offers)
 */
export async function getTriggeredPromotions(
  userId: string,
  venueId: string,
  status: 'ACTIVE' | 'USED' | 'EXPIRED' = 'ACTIVE'
): Promise<UserTriggeredPromotion[]> {
  const params = new URLSearchParams({ venueId, status });
  return api.get<UserTriggeredPromotion[]>(`/users/${userId}/triggered-promotions?${params.toString()}`);
}

/**
 * Track impressions for promotions (batch)
 * Call this when promotions are shown in the UI
 */
export async function trackImpressions(
  promotionIds: string[],
  venueId: string,
  userId?: string
): Promise<{ tracked: number }> {
  if (promotionIds.length === 0) {
    return { tracked: 0 };
  }
  return api.post<{ tracked: number }>('/promotions/impressions', {
    promotionIds,
    venueId,
    userId
  });
}

